import axios from 'axios'
import useStore from './store'
import useIntegration from './integration'
import useLocalStorage from './useLocalStorage'
import { useRoute, useRouter } from 'vue-router'
import useGlobalConfig from './globalConfig'

import type { LoyaltyMerchantForms } from '../types/global'
import type { dropdownValues } from '../types/dropdown'
import type { LoyaltyNotificationsConfigFields, LoyaltyPointsConfigForm, MerchantConfigs } from '@/types/types'
import type { Store } from '@/views/contests/Components/types/global'

const activeStep = ref(1)
const activeDirection = ref('forward')
const email = ref('')
const password = ref('')
const radioValue = ref(null)
const selectedPartner = ref(null)
const isAssociatedArray = ref([])
const partnerDevices = ref([])
const selectedStores = ref([])
const internalTokenPartners = ['tillersystems']
const tokenPartners = ['zelty']
const partnerWithCustomWebsiteUrl = ['shopify']
const posDeviceId = ref([])

export default function useLoyalty() {
  const { t } = useI18n()
  const { objectiveSlug } = useStore()
  const { availableOnboardingIntegrations } = useIntegration()
  const { globalConfig, stores, user } = useLocalStorage()
  const router = useRouter()
  const route = useRoute()
  const { updateKeys } = useGlobalConfig()

  const merchantConfigs = ref<MerchantConfigs>({})

  const formData = ref<LoyaltyMerchantForms>({
    defaultForm: {
      configs: {
        checkinPoints: {
          label: t('pages.settings.merchantForm.config.checkinPoints'),
          value: 10,
          type: 'number',
          required: true,
          min: 0
        },
        hasPointsByCurrency: {
          label: t('pages.settings.merchantForm.config.hasPointsByCurrency'),
          value: false,
          type: 'toggle',
          required: true
        },
        delayBetweenCheckins: {
          label: t('pages.settings.merchantForm.config.delayBetweenCheckins'),
          value: 7,
          type: 'number',
          required: true,
          min: 0
        },
        rewardTimeout: {
          label: t('pages.settings.merchantForm.config.rewardTimeout'),
          value: 15,
          type: 'number',
          required: true,
          min: 0
        },
        tabletPointsClaimingDelay: {
          label: t('pages.settings.merchantForm.config.pointsClaimingDelay'),
          value: 10,
          type: 'number',
          required: true,
          min: 0
        },
        hasExternalCustomers: {
          label: t('pages.settings.merchantForm.config.hasExternalCustomers'),
          value: false,
          type: 'toggle',
          required: true
        },
        hasPointsCanceledOnOrderCancelation: {
          label: t('pages.settings.merchantForm.config.hasPointsCanceledOnOrderCancelation'),
          value: false,
          type: 'toggle',
          required: false
        },
        integrationPointsClaimingTitle: {
          label: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingTitle.label'),
          value: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingTitle.value'),
          type: 'text',
          required: true
        },
        integrationPointsClaimingSubtitle: {
          label: t('pages.settings.merchantForm.text.orderTextsFields.pointsClaimingSubtitle.label'),
          value: t('pages.settings.merchantForm.text.orderTextsFields.pointsClaimingSubtitle.value'),
          type: 'text',
          required: true
        },
        loyaltyDeliveryOrderMessageChannel: {
          label: '',
          value: 'none',
          type: '',
          required: true
        }
      },
      media: {
        logo: {
          type: 'image',
          isCroppable: false,
          name: 'logo',
          label: 'Logo',
          pictureUrl: null,
          allowCrop: false,
          rules: 'image'
        },
        logoMail: {
          type: 'image',
          isCroppable: false,
          name: 'logoMail',
          label: 'Logo mail',
          pictureUrl: null,
          allowCrop: false,
          rules: 'image'
        },
        coverA: {
          type: 'image',
          isCroppable: false,
          name: 'coverA',
          label: 'Cover A',
          pictureUrl: null,
          allowCrop: true,
          rules: 'image'
        },
        coverB: {
          type: 'image',
          isCroppable: false,
          name: 'coverB',
          label: 'Cover B',
          pictureUrl: null,
          allowCrop: true,
          rules: 'image'
        },
        coverC: {
          type: 'image',
          isCroppable: false,
          name: 'coverC',
          label: 'Cover C',
          pictureUrl: null,
          allowCrop: true,
          rules: 'image'
        },
        coverD: {
          type: 'image',
          isCroppable: false,
          name: 'coverD',
          label: 'Cover D',
          pictureUrl: null,
          allowCrop: true,
          rules: 'image'
        }
      },
      colors: {
        background: {
          label: 'Couleurs des fonds',
          value: '#999dc4',
          type: 'color',
          rules: 'required'
        },
        text: {
          label: 'Couleurs des textes',
          value: '#000000',
          type: 'color',
          rules: 'required'
        },
        button: {
          label: 'Couleurs des boutons',
          value: '#999dc4',
          type: 'color',
          rules: 'required'
        }
      },
      text: {
        homeTextsFields: {
          homeTitle: {
            label: t('pages.settings.merchantForm.text.homeTextsFields.homeTitle.label'),
            value: t('pages.settings.merchantForm.text.homeTextsFields.homeTitle.value'),
            type: 'text',
            rules: 'required'
          },
          homeSubtitle: {
            label: t('pages.settings.merchantForm.text.homeTextsFields.homeSubtitle.label'),
            value: t('pages.settings.merchantForm.text.homeTextsFields.homeSubtitle.value', {
              merchant: localStorage.merchantName
            }),
            type: 'text',
            rules: 'required'
          },
          homeButton: {
            label: t('pages.settings.merchantForm.text.homeTextsFields.homeButton.label'),
            value: t('pages.settings.merchantForm.text.homeTextsFields.homeButton.value'),
            type: 'text',
            rules: 'required'
          },
          homeButtonWithActiveIntegration: {
            label: t('pages.settings.merchantForm.text.homeTextsFields.homeButtonWithActiveIntegration.label'),
            value: t('pages.settings.merchantForm.text.homeTextsFields.homeButtonWithActiveIntegration.value'),
            type: 'text',
            rules: 'required'
          }
        },
        loginTextsFields: {
          loginTitle: {
            label: t('pages.settings.merchantForm.text.loginTextsFields.loginTitle.label'),
            value: t('pages.settings.merchantForm.text.loginTextsFields.loginTitle.value'),
            type: 'text',
            rules: 'required'
          },
          loginSubtitle: {
            label: t('pages.settings.merchantForm.text.loginTextsFields.loginSubtitle.label'),
            value: t('pages.settings.merchantForm.text.loginTextsFields.loginSubtitle.value'),
            type: 'text',
            rules: 'required'
          }
        },
        pointsTextsFields: {
          pointsTitle: {
            label: t('pages.settings.merchantForm.text.pointsTextsFields.pointsTitle.label'),
            value: t('pages.settings.merchantForm.text.pointsTextsFields.pointsTitle.value'),
            type: 'text',
            rules: 'required'
          },
          pointsSubtitle: {
            label: t('pages.settings.merchantForm.text.pointsTextsFields.pointsSubtitle.label'),
            value: t('pages.settings.merchantForm.text.pointsTextsFields.pointsSubtitle.value'),
            type: 'text',
            rules: 'required'
          }
        },
        orderTextsFields: {
          integrationPointsClaimingTitle: {
            label: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingTitle.label'),
            value: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingTitle.value'),
            type: 'text',
            rules: 'required'
          },
          integrationPointsClaimingSubtitle: {
            label: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingSubtitle.label'),
            value: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingSubtitle.value'),
            type: 'text',
            rules: 'required'
          },
          integrationFailedTitle: {
            label: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedTitle.label'),
            value: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedTitle.value'),
            type: 'text',
            rules: 'required'
          },
          integrationFailedSubtitle: {
            label: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedSubtitle.label'),
            value: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedSubtitle.value'),
            type: 'text',
            rules: 'required'
          }
        },
        rewardsTextsFields: {
          rewardsTitle: {
            label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsTitle.label'),
            value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsTitle.value'),
            type: 'text',
            rules: 'required'
          },
          rewardsSubtitle: {
            label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitle.label'),
            value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitle.value'),
            type: 'text',
            rules: 'required'
          },
          rewardsSubtitleNew: {
            label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitleNew.label'),
            value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitleNew.value'),
            type: 'text',
            rules: 'required'
          },
          rewardsText: {
            label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsText.label'),
            value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsText.value'),
            type: 'text',
            rules: 'required'
          },
          rewardsButton: {
            label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsButton.label'),
            value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsButton.value'),
            type: 'text',
            rules: 'required'
          },
          rewardsPopupConfirmTitle: {
            label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmTitle.label'),
            value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmTitle.value'),
            type: 'text',
            rules: 'required'
          },
          rewardsPopupConfirmSubtitle: {
            label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmSubtitle.label'),
            value: `${t(
              'pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmSubtitle.value1'
            )} {{points}} ${t(
              'pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmSubtitle.value2'
            )} {{reward}} ?`,
            type: 'text',
            rules: 'required'
          },
          rewardsPopupForbiddenTitle: {
            label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupForbiddenTitle.label'),
            value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupForbiddenTitle.value'),
            type: 'text',
            rules: 'required'
          }
        },
        rewardWonTextsFields: {
          congratsTitle: {
            label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsTitle.label'),
            value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsTitle.value'),
            type: 'text',
            rules: 'required'
          },
          congratsSubtitle: {
            label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitle.label'),
            value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitle.value'),
            type: 'text',
            rules: 'required'
          },
          congratsSubtitleSms: {
            label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitleSms.label'),
            value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitleSms.value'),
            type: 'text',
            rules: 'required'
          }
        },
        questionsTextsFields: {
          questionsOptin: {
            label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptin.label'),
            value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptin.value'),
            type: 'text',
            rules: 'required'
          },
          questionsOptinYes: {
            label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinYes.label'),
            value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinYes.value'),
            type: 'text',
            rules: 'required'
          },
          questionsOptinNo: {
            label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinNo.label'),
            value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinNo.value'),
            type: 'text',
            rules: 'required'
          },
          questionsOptinEmail: {
            label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinEmail.label'),
            value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinEmail.value'),
            type: 'text',
            rules: 'required'
          },
          questionsIntro: {
            label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntro.label'),
            value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntro.value'),
            type: 'text',
            rules: 'required'
          },
          questionsIntroNew: {
            label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntroNew.label'),
            value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntroNew.value'),
            type: 'text',
            rules: 'required'
          },
          enrichmentQuestionBirthday: {
            label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionBirthday.label'),
            value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionBirthday.value'),
            type: 'text',
            rules: 'required'
          },
          enrichmentQuestionFirstname: {
            label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionFirstname.label'),
            value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionFirstname.value'),
            type: 'text',
            rules: 'required'
          },
          enrichmentQuestionGender: {
            label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionGender.label'),
            value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionGender.value'),
            type: 'text',
            rules: 'required'
          },
          enrichmentQuestionEmail: {
            label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionEmail.label'),
            value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionEmail.value'),
            type: 'text',
            rules: 'required'
          },
          questionsAnswered: {
            label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsAnswered.label'),
            value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsAnswered.value'),
            type: 'text',
            rules: 'required'
          },
          questionsNotAnswered: {
            label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsNotAnswered.label'),
            value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsNotAnswered.value'),
            type: 'text',
            rules: 'required'
          }
        },
        uniqueRewardTexts: {
          congratsSms: {
            label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSms.label'),
            value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSms.value'),
            type: 'text',
            rules: 'required'
          },
          voucherSms: {
            label: t('pages.settings.merchantForm.text.rewardWonTextsFields.voucherSms.label'),
            value: t('pages.settings.merchantForm.text.rewardWonTextsFields.voucherSms.value'),
            type: 'text',
            rules: 'required'
          },
          congratsSmsNew: {
            label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSmsNew.label'),
            value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSmsNew.value'),
            type: 'text',
            rules: 'required'
          }
        }
      }
    },
    textFormFormal: {
      homeTextsFields: {
        homeTitle: {
          label: t('pages.settings.merchantForm.text.homeTextsFields.homeTitle.label'),
          value: t('pages.settings.merchantForm.text.homeTextsFields.homeTitle.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        homeSubtitle: {
          label: t('pages.settings.merchantForm.text.homeTextsFields.homeSubtitle.label'),
          value: t('pages.settings.merchantForm.text.homeTextsFields.homeSubtitle.valueFormal', {
            merchant: localStorage.merchantName
          }),
          type: 'text',
          rules: 'required'
        },
        homeButton: {
          label: t('pages.settings.merchantForm.text.homeTextsFields.homeButton.label'),
          value: t('pages.settings.merchantForm.text.homeTextsFields.homeButton.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        homeButtonWithActiveIntegration: {
          label: t('pages.settings.merchantForm.text.homeTextsFields.homeButtonWithActiveIntegration.label'),
          value: t('pages.settings.merchantForm.text.homeTextsFields.homeButtonWithActiveIntegration.valueFormal'),
          type: 'text',
          rules: 'required'
        }
      },
      loginTextsFields: {
        loginTitle: {
          label: t('pages.settings.merchantForm.text.loginTextsFields.loginTitle.label'),
          value: t('pages.settings.merchantForm.text.loginTextsFields.loginTitle.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        loginSubtitle: {
          label: t('pages.settings.merchantForm.text.loginTextsFields.loginSubtitle.label'),
          value: t('pages.settings.merchantForm.text.loginTextsFields.loginSubtitle.valueFormal'),
          type: 'text',
          rules: 'required'
        }
      },
      pointsTextsFields: {
        pointsTitle: {
          label: t('pages.settings.merchantForm.text.pointsTextsFields.pointsTitle.label'),
          value: t('pages.settings.merchantForm.text.pointsTextsFields.pointsTitle.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        pointsSubtitle: {
          label: t('pages.settings.merchantForm.text.pointsTextsFields.pointsSubtitle.label'),
          value: t('pages.settings.merchantForm.text.pointsTextsFields.pointsSubtitle.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        marketingBonusPointsTitle: {
          label: t('pages.settings.merchantForm.text.pointsTextsFields.marketingBonusPointsTitle.label'),
          value: t('pages.settings.merchantForm.text.pointsTextsFields.marketingBonusPointsTitle.value'),
          type: 'text',
          rules: 'required'
        },
        marketingBonusPointsSubtitle: {
          label: t('pages.settings.merchantForm.text.pointsTextsFields.marketingBonusPointsSubtitle.label'),
          value: t('pages.settings.merchantForm.text.pointsTextsFields.marketingBonusPointsSubtitle.value'),
          type: 'text',
          rules: 'required'
        }
      },
      orderTextsFields: {
        integrationPointsClaimingTitle: {
          label: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingTitle.label'),
          value: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingTitle.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        integrationPointsClaimingSubtitle: {
          label: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingSubtitle.label'),
          value: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingSubtitle.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        integrationFailedTitle: {
          label: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedTitle.label'),
          value: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedTitle.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        integrationFailedSubtitle: {
          label: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedSubtitle.label'),
          value: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedSubtitle.valueFormal'),
          type: 'text',
          rules: 'required'
        }
      },
      rewardsTextsFields: {
        rewardsTitle: {
          label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsTitle.label'),
          value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsTitle.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        rewardsSubtitle: {
          label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitle.label'),
          value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitle.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        rewardsSubtitleNew: {
          label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitleNew.label'),
          value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitleNew.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        rewardsText: {
          label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsText.label'),
          value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsText.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        rewardsButton: {
          label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsButton.label'),
          value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsButton.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        rewardsPopupConfirmTitle: {
          label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmTitle.label'),
          value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmTitle.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        rewardsPopupConfirmSubtitle: {
          label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmSubtitle.label'),
          value: `${t(
            'pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmSubtitle.valueFormal1'
          )} {{points}} ${t(
            'pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmSubtitle.value2'
          )} {{reward}} ?`,
          type: 'text',
          rules: 'required'
        },
        name: {
          label: t('pages.settings.merchantForm.text.rewardsTextsFields.name.label'),
          value: t('pages.settings.merchantForm.text.rewardsTextsFields.name.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        rewardsPopupForbiddenTitle: {
          label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupForbiddenTitle.label'),
          value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupForbiddenTitle.valueFormal'),
          type: 'text',
          rules: 'required'
        }
      },
      rewardWonTextsFields: {
        congratsTitle: {
          label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsTitle.label'),
          value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsTitle.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        congratsSubtitle: {
          label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitle.label'),
          value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitle.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        congratsSubtitleSms: {
          label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitleSms.label'),
          value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitleSms.valueFormal'),
          type: 'text',
          rules: 'required'
        }
      },
      questionsTextsFields: {
        questionsOptin: {
          label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptin.label'),
          value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptin.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        questionsOptinYes: {
          label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinYes.label'),
          value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinYes.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        questionsOptinNo: {
          label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinNo.label'),
          value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinNo.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        questionsOptinEmail: {
          label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinEmail.label'),
          value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinEmail.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        questionsIntro: {
          label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntro.label'),
          value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntro.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        questionsIntroNew: {
          label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntroNew.label'),
          value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntroNew.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        enrichmentQuestionBirthday: {
          label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionBirthday.label'),
          value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionBirthday.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        enrichmentQuestionFirstname: {
          label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionFirstname.label'),
          value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionFirstname.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        enrichmentQuestionGender: {
          label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionGender.label'),
          value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionGender.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        enrichmentQuestionEmail: {
          label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionEmail.label'),
          value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionEmail.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        questionsAnswered: {
          label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsAnswered.label'),
          value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsAnswered.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        questionsNotAnswered: {
          label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsNotAnswered.label'),
          value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsNotAnswered.valueFormal'),
          type: 'text',
          rules: 'required'
        }
      },
      uniqueRewardTexts: {
        congratsSms: {
          label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSms.label'),
          value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSms.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        voucherSms: {
          label: t('pages.settings.merchantForm.text.rewardWonTextsFields.voucherSms.label'),
          value: t('pages.settings.merchantForm.text.rewardWonTextsFields.voucherSms.valueFormal'),
          type: 'text',
          rules: 'required'
        },
        congratsSmsNew: {
          label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSmsNew.label'),
          value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSmsNew.valueFormal'),
          type: 'text',
          rules: 'required'
        }
      }
    }
  })

  const loyaltyRouteName = computed(() => {
    if (objectiveSlug.value === 'loyalty-program') {
      const loyaltyStatus = globalConfig.value.merchant?.dematerializedLoyaltyStatus
      if (!loyaltyStatus) {
        return 'LoyaltyCreate'
      }
    }
    return 'LoyaltyEdit'
  })

  const goToStep = async (isForward: boolean) => {
    if (isForward) {
      activeDirection.value = 'forward'
      activeStep.value++
    } else {
      activeDirection.value = 'back'
      activeStep.value--
    }

    const queryData = {
      step: activeStep.value.toString(),
      type: route.query?.type,
      partnerId: selectedPartner.value?.id,
      partnerSlug: selectedPartner.value?.partner?.slug
    }
    await router.push({ query: queryData })
  }

  const goToExplanation = (isEntering: boolean) => {
    activeDirection.value = ''
    if (isEntering) {
      activeStep.value = 0
    } else {
      activeStep.value = 3
    }
  }

  const radioGroup = computed(() => {
    return availableOnboardingIntegrations.map((integration) => ({
      id: integration.slug,
      img: integration.logo
    }))
  })

  const handleLoyaltyClick = () => {
    updateKeys('merchants', {
      dematerializedLoyaltyStatus: 2
    })
    router.push({ name: 'Home' })
  }

  const updateSelectedStores = () => {
    selectedStores.value = partnerDevices.value.reduce((acc, p) => {
      acc[p.id] = {
        id: stores.value[0]?.id,
        name: stores.value[0]?.name
      }
      return acc
    }, {})
  }

  const storesValues = computed<dropdownValues[]>(() => {
    return stores.value.map((store: Store) => {
      return {
        id: store.id,
        text: store.name
      }
    })
  })

  const getPartnerDevices = async () => {
    const { data } = await axios.get('/partner-devices', {
      params: {
        partner: selectedPartner.value.partner.slug,
        storeId: stores.value[0].id
      }
    })
    partnerDevices.value = data.data
  }

  const updateStore = (deviceId: number, selectedStore: number) => {
    selectedStores.value[deviceId].id = selectedStore
    selectedStores.value[deviceId].name = stores.value.find((s) => s.id === selectedStore)?.name
  }

  const loyaltyPointsConfigFields = ref<LoyaltyPointsConfigForm>({
    globals: {
      title: t('pages.loyaltyPage.tabs.points.subtitles.global'),
      fields: {
        bonusPoints: {
          label: t('pages.settings.merchantForm.config.bonusPoints'),
          value: 10,
          type: 'number',
          rules: 'required',
          required: true,
          min: 0,
          integerOnly: true
        },
        loyaltyOrderMinimumAmount: {
          label: t('pages.settings.merchantForm.config.loyaltyOrderMinimumAmount'),
          value: 0,
          type: 'number',
          rules: 'required',
          required: true,
          min: 0
        },
        pointsLabel: {
          label: t('pages.settings.merchantForm.config.pointsLabel'),
          value: 'points',
          type: 'text',
          rules: 'required',
          required: true,
          minLength: 3,
          maxLength: 16,
          hasToolTip: true,
          integerOnly: true
        },
        hasPointsCanceledOnOrderCancelation: {
          label: t('pages.settings.merchantForm.config.hasPointsCanceledOnOrderCancelation'),
          value: false,
          type: 'toggle',
          rules: 'optional',
          required: false,
          toolTip: t('pages.settings.merchantForm.config.hasPointsCanceledOnOrderCancelationTooltip'),
          isSupportConnectionOnly: true
        }
      }
    },
    perVisit: {
      title: t('pages.loyaltyPage.tabs.points.subtitles.perCheckin'),
      fields: {
        checkinPoints: {
          label: t('pages.settings.merchantForm.config.checkinPoints'),
          value: 10,
          type: 'number',
          rules: 'required',
          required: true,
          min: 0,
          integerOnly: true
        },
        delayBetweenCheckins: {
          label: t('pages.settings.merchantForm.config.delayBetweenCheckins'),
          value: 7,
          type: 'number',
          rules: 'required',
          required: true,
          min: 0,
          integerOnly: true
        }
      }
    },
    perAmountSpent: {
      title: t('pages.loyaltyPage.tabs.points.subtitles.perAmountSpent'),
      hasToggle: true,
      toggleValue: false,
      fields: {
        loyaltyPointsByCurrencyUnit: {
          label: t('pages.settings.merchantForm.config.loyaltyPointsByCurrencyUnit'),
          value: 10,
          type: 'number',
          rules: 'required',
          required: true,
          min: 0,
          integerOnly: true
        }
      }
    }
  })

  const loyaltyNotificationsConfigFields = ref<LoyaltyNotificationsConfigFields>({
    smsName: {
      label: t('pages.settings.merchantForm.infos.smsName'),
      value: user.value.merchant.smsName,
      type: 'text',
      required: true,
      disabled: true,
      minLength: 3,
      maxLength: 11,
      toolTip: t('pages.settings.merchantForm.infos.changeSenderName')
    },
    hasNotificationAfterOrder: {
      label: t('pages.marketplace.marketplacePage.form.settings.messageChannel.title'),
      value: merchantConfigs.value.loyaltyDeliveryOrderMessageChannel !== null,
      type: 'toggle',
      required: true
    },
    smsAtSubscription: {
      label: t('pages.settings.merchantForm.notifications.sendSmsAtSubscription.label'),
      value: t('pages.settings.merchantForm.notifications.sendSmsAtSubscription.defaultContent', {
        shortLinkTag: '{{customerApp}}'
      }),
      type: 'toggle',
      required: true
    },
    loyaltyDeliveryOrderMessageChannel: {
      options: [
        {
          id: 1,
          value: 'sms',
          title: t('pages.marketplace.marketplacePage.form.settings.messageChannel.sms.title'),
          description: t('pages.marketplace.marketplacePage.form.settings.messageChannel.sms.description')
        },
        {
          id: 2,
          value: 'mail',
          title: t('pages.marketplace.marketplacePage.form.settings.messageChannel.mail.title'),
          description: t('pages.marketplace.marketplacePage.form.settings.messageChannel.mail.description')
        }
      ],
      value: {
        id: null,
        title: null
      },
      type: 'radio',
      required: true
    },
    orderReceivedTextsFields: {
      integrationAfterOrderMailSubject: {
        label: t('pages.settings.merchantForm.text.orderReceivedTextsFields.integrationAfterOrderMailSubject.label'),
        value: '',
        notificationChannel: 'mail',
        type: 'textarea',
        rules: 'required',
        maxLength: 78
      },
      integrationAfterOrderMailBody: {
        label: t('pages.settings.merchantForm.text.integrationAfterOrderMailBody.integrationAfterOrderMailBody.label'),
        value: '',
        notificationChannel: 'mail',
        type: 'textarea',
        rules: 'required',
        maxLength: 10000
      },
      integrationAfterOrderSms: {
        label: t('pages.settings.merchantForm.text.orderReceivedTextsFields.integrationAfterOrderSms.label'),
        value: '',
        notificationChannel: 'sms',
        type: 'textarea',
        rules: 'required',
        maxLength: 145
      }
    }
  })

  return {
    formData,
    loyaltyRouteName,
    activeStep,
    activeDirection,
    goToStep,
    email,
    password,
    radioGroup,
    goToExplanation,
    radioValue,
    selectedPartner,
    isAssociatedArray,
    partnerDevices,
    handleLoyaltyClick,
    selectedStores,
    storesValues,
    updateStore,
    getPartnerDevices,
    updateSelectedStores,
    internalTokenPartners,
    tokenPartners,
    partnerWithCustomWebsiteUrl,
    posDeviceId,
    loyaltyPointsConfigFields,
    loyaltyNotificationsConfigFields
  }
}
